import { ReactNode } from 'react';
import { AlertDialog as RxAlertDialog, VisuallyHidden } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { withStyles, Title2 as Title, MainButton as Button } from 'vopay';
import styles from './AlertDialog.module.scss';

const cn = withStyles(styles);

export type AlertDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onAbort?: () => void;
  onConfirm?: () => void;
  title?: ReactNode;
  content?: ReactNode;
  abortLabel?: ReactNode;
  confirmLabel?: ReactNode;
  top?: ReactNode;
  closeButton?: boolean;
};

export default function AlertDialog({
  isOpen,
  title,
  content,
  onClose,
  onAbort,
  onConfirm,
  abortLabel,
  confirmLabel,
  top,
  closeButton = true,
}: AlertDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared' });

  return (
    <RxAlertDialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <RxAlertDialog.Overlay data-testid="alert-dialog-overlay" className={cn('alertDialogOverlay')} />

      {/* Radix needs a Title for the accessible name and a Description (or an explicit
          aria-describedby={undefined}) — both props are optional here, so cover the missing cases. */}
      <RxAlertDialog.Content
        data-testid="alert-dialog"
        className={cn('alertDialogContainer')}
        {...(content ? {} : { 'aria-describedby': undefined })}
      >
        <div className={cn('top')}>
          {top}

          <div className={cn('info')}>
            {title ? (
              <RxAlertDialog.Title asChild>
                <Title level="3">{title}</Title>
              </RxAlertDialog.Title>
            ) : (
              <VisuallyHidden.Root asChild>
                <RxAlertDialog.Title>{t('AlertDialog.fallbackTitle', { defaultValue: 'Alert' })}</RxAlertDialog.Title>
              </VisuallyHidden.Root>
            )}

            {content && (
              <RxAlertDialog.Description asChild>
                <div className={cn('description')}>{content}</div>
              </RxAlertDialog.Description>
            )}
          </div>
        </div>

        {abortLabel || confirmLabel ? (
          <div className={cn('controls')}>
            {abortLabel && (
              <RxAlertDialog.Cancel asChild onClick={onAbort}>
                <Button colorScheme="secondary">{abortLabel}</Button>
              </RxAlertDialog.Cancel>
            )}

            {confirmLabel && (
              <RxAlertDialog.Action asChild onClick={onConfirm}>
                <Button colorScheme="primary">{confirmLabel}</Button>
              </RxAlertDialog.Action>
            )}
          </div>
        ) : closeButton ? (
          <RxAlertDialog.Cancel asChild>
            <Button colorScheme="secondary">{t('Buttons.close', { defaultValue: 'Close' })}</Button>
          </RxAlertDialog.Cancel>
        ) : null}
      </RxAlertDialog.Content>
    </RxAlertDialog.Root>
  );
}
